import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from './AuthContext.jsx'
import * as api from '../db/api.js'
import { ConfirmModal } from '../components/UX.jsx'

const Ctx = createContext(null)

const today = new Date()
const fmt = (d) => d.toISOString().split('T')[0]
const dAgo = (n) => {
  const d = new Date(today)
  d.setDate(d.getDate() - n)
  return d
}

function getDisplayName(user) {
  return user?.user_metadata?.full_name || user?.email || 'Usuário'
}

function getInitials(name) {
  return (name || 'U')
    .split(/\s+/)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() || '')
    .join('')
}

function getRoleLabel(role) {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'admin':
      return 'Administrador'
    case 'manager':
      return 'Gestor'
    case 'viewer':
      return 'Leitor'
    case 'editor':
      return 'Editor'
    case 'gestor':
      return 'Gestor'
    case 'responsavel':
      return 'Responsavel'
    case 'comum':
      return 'Usuario'
    default:
      return 'Membro'
  }
}

// Sentinela de localStorage que indica "usuário optou pelo scope agregado
// (todos os clientes acessíveis)". Persiste a intenção do usuário entre
// sessões para que ele não precise re-selecionar a cada login.
const AGGREGATE_SCOPE_TOKEN = '__all__'

function avatarStorageKey(userId) {
  // Foto de perfil persistida localmente (base64). Sem endpoint dedicado de
  // avatar no backend, mantemos por usuário no localStorage para refletir
  // imediatamente na UI e sobreviver a reloads.
  return userId ? `deebs:avatar:${userId}` : null
}

function activeOrganizationStorageKey(userId) {
  return userId ? `deebs:activeOrganization:${userId}` : null
}

function readPersistedActiveOrganization(userId) {
  const key = activeOrganizationStorageKey(userId)
  if (!key) return null
  try {
    return localStorage.getItem(key) || null
  } catch {
    return null
  }
}

function persistActiveOrganization(userId, valueOrNull) {
  const key = activeOrganizationStorageKey(userId)
  if (!key) return
  try {
    if (valueOrNull) localStorage.setItem(key, valueOrNull)
    else localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function selectedClientStorageKey(userId) {
  // Mantemos a chave legada `deebs:activeClient:*` para preservar a seleção
  // que usuários já têm persistida no navegador. Renomear quebraria UX.
  return userId ? `deebs:activeClient:${userId}` : null
}

function readPersistedSelectedClient(userId) {
  const key = selectedClientStorageKey(userId)
  if (!key) return null
  try {
    return localStorage.getItem(key) || null
  } catch {
    return null
  }
}

function persistSelectedClient(userId, valueOrNull) {
  // Persistência:
  //   - clientId  → grava o uuid
  //   - AGGREGATE_SCOPE_TOKEN → grava a sentinela
  //   - null/undefined → remove (boot decide pelo default)
  const key = selectedClientStorageKey(userId)
  if (!key) return
  try {
    if (valueOrNull) localStorage.setItem(key, valueOrNull)
    else localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function resolveInitialSelectedClient(clientsList, userId, accessMode) {
  // Retorna:
  //   - null se não houver clientes
  //   - null se o usuário já optou pelo agregado (sentinela __all__)
  //   - null se for org/hybrid sem persistência E houver múltiplos clientes
  //     (Fase 5 / PR-3: default agregado para usuários com visão de org)
  //   - um Client (object) caso contrário
  //
  // O chamador distingue "sem clientes" (clientsList.length === 0) de
  // "scope agregado" via tamanho da lista.
  if (!clientsList || clientsList.length === 0) return null

  const persisted = readPersistedSelectedClient(userId)

  // Persistência sempre tem prioridade — respeita a escolha do usuário.
  if (persisted === AGGREGATE_SCOPE_TOKEN) return null
  if (persisted) {
    const found = clientsList.find((c) => c.id === persisted)
    if (found) return found
    // se o id persistido não existe mais (cliente removido/revogado),
    // segue para o fallback abaixo
  }

  // Default agregado para QUALQUER usuário com múltiplos clientes acessíveis.
  // Antes restrito a org/hybrid; agora inclui client_only (usuário convidado
  // que recebeu acesso a vários clients via user_client_access) — ele deve ter
  // o mesmo comportamento do owner: agregado por padrão + poder selecionar
  // qualquer client. Com 1 só cliente, cai no fallback abaixo (seleção única,
  // para `selectedClient.role` alimentar `useAccess`).
  if (clientsList.length > 1) {
    return null
  }

  const selfClient = clientsList.find((c) => c.is_self)
  if (selfClient) return selfClient
  return clientsList[0]
}

// Issue #94: resolve a organização ativa a partir das memberships recém
// carregadas de /api/me/access. Roda DENTRO de `loadAccess`, antes de
// `getClients()`/`getAccounts()` — precisa terminar antes dessas chamadas
// pra que a primeira leitura da sessão já saia com o escopo certo (senão o
// primeiro /api/clients da sessão volta agregando todas as orgs do usuário).
//
// Sem tela de escolha bloqueando o login (removida — o seletor no topbar já
// mostra e deixa trocar a organização ativa a qualquer momento, então uma
// segunda UI só pra decidir isso no boot virou redundante): com >1
// organização e nenhuma escolha persistida, cai na primeira da lista — o
// usuário vê qual é no seletor e troca dali se não for a que queria.
function resolveInitialActiveOrganization(membershipsList, userId) {
  if (!membershipsList || membershipsList.length === 0) return null

  const persisted = readPersistedActiveOrganization(userId)
  if (persisted && membershipsList.some((m) => m.organization_id === persisted)) {
    return persisted
  }

  return membershipsList[0].organization_id
}

export function AppProvider({ children }) {
  const { listOrganizations, signOut, user: authUser } = useAuth()
  const [org, setOrg] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)

  // Fase 4: estado do AccessContext consumido de /api/me/access.
  const [access, setAccess] = useState({
    user: null,
    accessMode: null,        // "org" | "hybrid" | "client_only" | "none" | null
    memberships: [],
    clientGrants: [],
    orgVisibleIds: [],
    effectiveClientIds: [],
  })
  const [availableClients, setAvailableClients] = useState([])
  // Fase 5: `selectedClient` substitui semanticamente `activeClient`.
  //   - null  → scope agregado (todos os clientes acessíveis)
  //   - Client object → filtrar apenas esse cliente
  // O alias `activeClient` é mantido no value do Provider para compatibilidade.
  const [selectedClient, setSelectedClientState] = useState(null)

  // Issue #94: TODAS as organizações do usuário (qualquer role) — o gate de
  // login e o seletor de organização ativa operam sobre esse conjunto, não
  // só as que o usuário administra (o vazamento de dados entre orgs afeta
  // qualquer membro multi-org, não só owner/admin). Resolução do valor
  // inicial mora em `loadAccess` (precisa terminar ANTES de `/api/clients`
  // ser chamado, senão a primeira leitura da sessão sai sem escopo).
  const organizations = useMemo(
    () => (access.memberships || []).filter((m) => m.is_active !== false),
    [access.memberships]
  )
  const [activeOrganizationId, setActiveOrganizationIdState] = useState(null)

  const accessMode = access.accessMode

  const user = useMemo(() => {
    const name = getDisplayName(authUser)
    // Fase 4: o role exibido vem do role do selectedClient quando aplicável
    // (refletindo o que o AccessContext já decidiu no backend), com
    // fallback para a primeira membership ou metadados Supabase.
    const clientRole = selectedClient?.role
    const membershipRole = access.memberships?.[0]?.role
    const role =
      clientRole ||
      membershipRole ||
      authUser?.app_metadata?.role ||
      authUser?.user_metadata?.role ||
      'membro'
    return {
      id: authUser?.id,
      email: authUser?.email,
      name,
      avatar: getInitials(name) || '?',
      role,
      roleLabel: getRoleLabel(role),
    }
  }, [authUser, selectedClient, access.memberships])

  // Foto de perfil (base64 data URL) — carregada do localStorage por usuário.
  const [avatarUrl, setAvatarUrl] = useState(null)
  useEffect(() => {
    const key = avatarStorageKey(authUser?.id)
    if (!key) {
      setAvatarUrl(null)
      return
    }
    try {
      setAvatarUrl(localStorage.getItem(key) || null)
    } catch {
      setAvatarUrl(null)
    }
  }, [authUser?.id])

  const updateAvatar = useCallback((dataUrl) => {
    setAvatarUrl(dataUrl || null)
    const key = avatarStorageKey(authUser?.id)
    if (!key) return
    try {
      if (dataUrl) localStorage.setItem(key, dataUrl)
      else localStorage.removeItem(key)
    } catch {
      /* ignore (quota) */
    }
  }, [authUser?.id])

  // ── Sidebar recolhível ──────────────────────────────────────────────────
  // `sidebarCollapsed` = preferência MANUAL do usuário (botão de recolher),
  // persistida em localStorage para sobreviver a reloads. Como o AppProvider
  // não remonta durante a navegação, o estado já se mantém entre páginas; o
  // localStorage cobre reloads/nova aba.
  //
  // `sidebarForcedCollapsed` = recolhimento AUTOMÁTICO imposto por uma página
  // (ex.: calendário que não cabe com a sidebar aberta). NÃO é persistido: é
  // efêmero e some quando a página desmonta ou volta a caber (via o hook
  // useCollapseSidebarOnOverlap). Mantê-lo separado da preferência manual
  // evita sobrescrever a escolha do usuário — ao sair da página o estado
  // manual volta intacto.
  //
  // O recolhimento EFETIVO (o que a UI aplica) = manual OR forçado.
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    try {
      return localStorage.getItem('deebs:sidebarCollapsed') === '1'
    } catch {
      return false
    }
  })
  const setSidebarCollapsed = useCallback((valueOrFn) => {
    setSidebarCollapsedState((prev) => {
      const next = typeof valueOrFn === 'function' ? valueOrFn(prev) : valueOrFn
      try {
        localStorage.setItem('deebs:sidebarCollapsed', next ? '1' : '0')
      } catch {
        /* ignore (quota / modo privado) */
      }
      return next
    })
  }, [])
  const toggleSidebar = useCallback(
    () => setSidebarCollapsed((prev) => !prev),
    [setSidebarCollapsed]
  )
  const [sidebarForcedCollapsed, setSidebarForcedCollapsed] = useState(false)

  const [selectedAccounts, setSelectedAccounts] = useState([])
  // Contas Meta escolhidas nas telas de Meta Ads; pertence ao cliente selecionado.
  const [metaAccountIds, setMetaAccountIds] = useState([])
  const [period, setPeriod] = useState({ start: fmt(dAgo(6)), end: fmt(today) })
  const [periodLabel, setPeriodLabel] = useState('Últimos 7 dias')

  const [toasts, setToasts] = useState([])
  const toast = useCallback((msg, type = 'info', duration = 4000) => {
    const id = Date.now()
    setToasts((t) => [...t, { id, msg, type }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), duration)
  }, [])
  const removeToast = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  // Confirmação com componente próprio (substitui window.confirm). Retorna
  // Promise<boolean>: `await confirm({ title, message, danger, confirmText })`.
  const [confirmState, setConfirmState] = useState(null)
  const confirm = useCallback(
    (opts) => new Promise((resolve) => setConfirmState({ opts: opts || {}, resolve })),
    [],
  )
  const _resolveConfirm = (result) => {
    setConfirmState((s) => { s?.resolve(result); return null })
  }

  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const addNotification = useCallback((notification) => {
    const id = Date.now()
    setNotifications((prev) => [
      { id, ...notification, read: false, time: new Date() },
      ...prev,
    ])
    setUnreadCount((c) => c + 1)
  }, [])
  const markNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnreadCount(0)
  }, [])

  const [comments, setComments] = useState({})
  const addComment = useCallback(
    (entityType, entityId, text) => {
      const id = Date.now()
      setComments((prev) => {
        const key = `${entityType}:${entityId}`
        const existing = prev[key] || []
        return {
          ...prev,
          [key]: [
            ...existing,
            { id, text, author: user.name || 'Anônimo', avatar: user.avatar || '?', time: new Date() },
          ],
        }
      })
    },
    [user]
  )

  const [assignments, setAssignments] = useState({})
  const assignTask = useCallback(
    (entityType, entityId, assignee) => {
      setAssignments((prev) => ({ ...prev, [`${entityType}:${entityId}`]: assignee }))
      toast(`Tarefa atribuída a ${assignee}`, 'success')
    },
    [toast]
  )

  const [onlineUsers] = useState([])

  const setPeriodPreset = useCallback((label, days) => {
    setPeriodLabel(label)
    setPeriod({ start: fmt(dAgo(days - 1)), end: fmt(today) })
  }, [])

  const logout = useCallback(async () => {
    // Encerra a sessão no Supabase (limpa o token persistido) e zera o
    // sessionStorage para não deixar resíduos da sessão atual. O caller
    // (Profile) redireciona para /login após o await.
    await signOut()
    try {
      sessionStorage.clear()
    } catch {
      /* ignore */
    }
  }, [signOut])

  // Fase 4/5: troca do scope ativo.
  //
  // Aceita:
  //   - <uuid de cliente>: filtra apenas esse cliente; envia ?client_id=<uuid>
  //     em todas as requests subsequentes.
  //   - null: muda para scope agregado; requests passam a NÃO carregar
  //     client_id e o backend agrega todos os effective_client_ids.
  //
  // Em ambos os casos:
  //   - atualiza o singleton em db/api.js (null → desliga injeção);
  //   - persiste em localStorage (id real ou sentinela AGGREGATE_SCOPE_TOKEN);
  //   - zera `selectedAccounts` para evitar pílulas órfãs (B4);
  //   - refaz `getAccounts()` (com ou sem client_id, conforme o caso).
  const setSelectedClient = useCallback(
    async (clientIdOrNull) => {
      let nextClient
      let persistValue

      if (clientIdOrNull === null) {
        // Scope agregado opt-in.
        nextClient = null
        persistValue = AGGREGATE_SCOPE_TOKEN
      } else {
        const found = availableClients.find((c) => c.id === clientIdOrNull)
        if (!found) return // id desconhecido — ignora, mantém estado atual
        nextClient = found
        persistValue = found.id
      }

      setSelectedClientState(nextClient)
      api.setSelectedClientId(nextClient?.id || null)
      persistSelectedClient(authUser?.id, persistValue)
      setSelectedAccounts([])
      setMetaAccountIds([])

      try {
        const accountsData = await api.getAccounts()
        setAccounts(accountsData || [])
      } catch (err) {
        console.warn('[app] getAccounts falhou ao mudar scope:', err.message)
        setAccounts([])
      }
    },
    [availableClients, authUser?.id]
  )

  // Alias legado mantido para compatibilidade.
  // Componentes existentes (ClientSwitcher, páginas) continuam usando
  // `setActiveClient`. Remover na limpeza pós-validação.
  const setActiveClient = setSelectedClient

  // Boot/refresh do AccessContext. Extraído num useCallback para ser reusado
  // tanto pelo useEffect de boot quanto por `refreshAccess()` — chamado após
  // o onboarding criar a org, quando o AppProvider NÃO remonta (a navegação
  // /onboarding → /dashboard fica sob o mesmo ProtectedRoute) e portanto o
  // estado `access` precisa ser re-hidratado manualmente.
  //
  // `forceRefresh` propaga ?refresh=true para o backend, furando o cache TTL.
  // `isActive` permite ao boot abortar setState após unmount (cancelamento).
  const loadAccess = useCallback(
    async ({ forceRefresh = false, isActive = () => true } = {}) => {
      if (!authUser) return

      let accessData = null
      try {
        accessData = await api.getMyAccess({ refresh: forceRefresh })
      } catch (accessErr) {
        console.warn('[app] /api/me/access falhou, caindo no caminho legado:', accessErr.message)
      }

      if (!isActive()) return

      // accessMode resolvido nesta passada — retornado ao final para que
      // `refreshAccess()` possa confirmar o vínculo (ex.: onboarding só navega
      // se o acesso deixou de ser 'none').
      let resolvedMode = 'none'

      if (accessData) {
        // Caminho novo: AccessContext canônico.
        const memberships = accessData.memberships || []
        const clientGrants = accessData.clients || []
        setAccess({
          user: accessData.user || null,
          accessMode: accessData.access_mode || 'none',
          memberships,
          clientGrants,
          orgVisibleIds: accessData.org_visible_ids || [],
          effectiveClientIds: accessData.effective_client_ids || [],
        })

        // Issue #94: resolve e ATIVA o header de organização (api.js) antes
        // de getClients()/getAccounts() — essas chamadas precisam já sair
        // escopadas pela organização ativa (senão o primeiro /api/clients da
        // sessão volta agregando todas as orgs do usuário).
        const resolvedOrgId = resolveInitialActiveOrganization(memberships, authUser.id)
        setActiveOrganizationIdState(resolvedOrgId)
        api.setActiveOrganizationId(resolvedOrgId)

        let clientsData = []
        try {
          clientsData = await api.getClients()
        } catch (err) {
          console.warn('[app] /api/clients falhou:', err.message)
        }
        if (!isActive()) return
        setAvailableClients(clientsData || [])

        // resolved pode ser:
        //   - Client object  → scope single (cliente persistido, is_self, ou primeiro
        //                       para client_only / org com 1 cliente)
        //   - null           → scope agregado (usuário optou via __all__, OU é
        //                       org/hybrid com múltiplos clientes sem persistência)
        //                       OU não há clientes acessíveis
        //
        // PR-3: passamos accessMode (lido direto do payload, não do state que
        // foi acabado de chamar via setAccess e ainda não atualizou) para
        // permitir o default agregado em org/hybrid com >1 cliente.
        const bootAccessMode = accessData.access_mode || 'none'
        resolvedMode = bootAccessMode
        const resolved = resolveInitialSelectedClient(
          clientsData,
          authUser.id,
          bootAccessMode
        )
        setSelectedClientState(resolved)
        api.setSelectedClientId(resolved?.id || null)
        // Não sobrescrevemos a persistência no boot: respeitamos o que o
        // usuário escolheu no passado. Só persistimos em mudanças explícitas
        // via setSelectedClient(). Em particular, o "default agregado" do
        // PR-3 NÃO grava __all__ — ele só se torna persistido se/quando o
        // usuário voltar para esse modo via ClientSwitcher.

        // Hidrata `org` para compatibilidade com Layout/Dashboard/Profile.
        // Em client_only não há membership → org=null (UI já trata).
        const primary = memberships[0]
        if (primary) {
          try {
            const { data: orgs } = await listOrganizations()
            if (!isActive()) return
            const matched = orgs?.find((o) => o.id === primary.organization_id) || null
            setOrg(matched)
          } catch (err) {
            console.warn('[app] listOrganizations falhou (hidratação):', err.message)
            setOrg(null)
          }
        } else {
          setOrg(null)
        }

        // Buscar accounts se o usuário tem ao menos 1 cliente acessível.
        // Em scope agregado (resolved === null com clientsData.length > 0),
        // getAccounts vai sem client_id e o backend retorna o conjunto
        // completo de ad_accounts dos effective_client_ids.
        if (clientsData && clientsData.length > 0) {
          try {
            const accountsData = await api.getAccounts()
            if (!isActive()) return
            setAccounts(accountsData || [])
          } catch (err) {
            console.warn('[app] getAccounts falhou:', err.message)
            setAccounts([])
          }
        } else {
          setAccounts([])
        }
      } else {
        // Fallback legado: preserva comportamento atual quando /api/me/access
        // falha de forma inesperada (rede, backend fora do ar).
        try {
          const { data: organizations, error: organizationsError } = await listOrganizations()
          if (!isActive()) return
          if (organizationsError) {
            console.error('[app] listOrganizations falhou:', organizationsError)
            setOrg(null)
            setAccounts([])
            setAccess((prev) => ({ ...prev, accessMode: 'none' }))
            return
          }
          const currentOrg = organizations?.[0] ?? null
          setOrg(currentOrg)
          resolvedMode = currentOrg ? 'org' : 'none'
          setAccess((prev) => ({
            ...prev,
            accessMode: resolvedMode,
          }))
          if (!currentOrg) {
            setAccounts([])
            return
          }
          try {
            const accountsData = await api.getAccounts()
            if (!isActive()) return
            setAccounts(accountsData || [])
          } catch (apiErr) {
            console.warn('[app] getAccounts falhou, seguindo com accounts=[]:', apiErr.message)
            setAccounts([])
          }
        } catch (err) {
          console.error('[app] fallback legado falhou:', err)
          setAccess((prev) => ({ ...prev, accessMode: 'none' }))
        }
      }

      return resolvedMode
    },
    [listOrganizations, authUser]
  )

  // Re-hidrata o AccessContext furando o cache do backend. Chamado pelo
  // onboarding após criar a org via RPC: sem isso o AppProvider manteria
  // accessMode='none' (não remonta) e o AccessGate barraria o /dashboard.
  const refreshAccess = useCallback(
    () => loadAccess({ forceRefresh: true }),
    [loadAccess]
  )

  // Issue #94: troca a organização ativa da sessão. Atualiza o header em
  // toda request subsequente (api.js) e recarrega clients/accounts sob o
  // novo escopo — sem isso a UI continuaria mostrando dados da organização
  // anterior até um reload manual. `selectedClient` não precisa ser resetado
  // aqui à parte: `resolveInitialSelectedClient` já trata "client persistido
  // não existe mais na lista atual" caindo pro fallback agregado/único.
  const setActiveOrganizationId = useCallback(
    async (organizationId) => {
      if (!organizations.some((o) => o.organization_id === organizationId)) return
      setActiveOrganizationIdState(organizationId)
      persistActiveOrganization(authUser?.id, organizationId)
      api.setActiveOrganizationId(organizationId)
      await refreshAccess()
    },
    [organizations, authUser?.id, refreshAccess]
  )

  useEffect(() => {
    if (!authUser) return

    let active = true
    loadAccess({ isActive: () => active }).finally(() => {
      if (active) setLoading(false)
    })

    return () => {
      active = false
    }
  }, [authUser, loadAccess])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'Inter, sans-serif',
          color: '#6e6e73',
        }}
      >
        Carregando...
      </div>
    )
  }

  return (
    <Ctx.Provider
      value={{
        user,
        org,
        accounts,
        selectedAccounts,
        setSelectedAccounts,
        metaAccountIds,
        setMetaAccountIds,
        period,
        setPeriod,
        periodLabel,
        setPeriodLabel,
        setPeriodPreset,
        toasts,
        toast,
        removeToast,
        confirm,
        notifications,
        unreadCount,
        addNotification,
        markNotificationsRead,
        comments,
        addComment,
        assignments,
        assignTask,
        onlineUsers,
        avatarUrl,
        updateAvatar,
        logout,
        // Sidebar recolhível — estado manual + forçado (auto). A UI aplica
        // o OR dos dois; ver useCollapseSidebarOnOverlap para o auto.
        sidebarCollapsed,
        setSidebarCollapsed,
        toggleSidebar,
        sidebarForcedCollapsed,
        setSidebarForcedCollapsed,
        // Fase 4: AccessContext exposto.
        access,
        accessMode,
        refreshAccess,
        memberships: access.memberships,
        clientGrants: access.clientGrants,
        availableClients,
        // Issue #94: organização ativa da sessão — filtra toda leitura no
        // backend (header injetado em api.js). `organizations` é TODAS as
        // organizações do usuário (não só onde é owner/admin), usada pelo
        // gate de login e pelo OrganizationSwitcher.
        organizations,
        activeOrganizationId,
        setActiveOrganizationId,
        // Fase 5: selectedClient é a API canônica.
        //   - null   → scope agregado (todos os clientes acessíveis)
        //   - Client → scope single (apenas esse cliente)
        selectedClient,
        setSelectedClient,
        // Aliases legados — mantidos para compatibilidade com componentes
        // que ainda usam activeClient/setActiveClient. Mesmo valor/função.
        activeClient: selectedClient,
        setActiveClient,
      }}
    >
      {children}
      <ConfirmModal
        isOpen={!!confirmState}
        title={confirmState?.opts.title || 'Confirmar ação'}
        message={confirmState?.opts.message || ''}
        confirmText={confirmState?.opts.confirmText || 'Confirmar'}
        cancelText={confirmState?.opts.cancelText || 'Cancelar'}
        danger={confirmState?.opts.danger}
        onConfirm={() => _resolveConfirm(true)}
        onCancel={() => _resolveConfirm(false)}
      />
    </Ctx.Provider>
  )
}

export const useApp = () => useContext(Ctx)
